export interface QueryResult {
  columns: string[];
  rows: any[][];
  rowCount: number;
}

// Helper function to execute with proper connection management
async function withConnection<T>(db: any, callback: (connection: any) => Promise<T>): Promise<T> {
  if (!db) {
    throw new Error('DuckDB not initialized');
  }
  
  const connection = await db.connect();
  try {
    return await callback(connection);
  } finally {
    await connection.close();
  }
}

// Helper function to properly extract scalar values from DuckDB results
function extractScalarValue(result: any, columnIndex: number = 0, rowIndex: number = 0): any {
  try {
    // Try multiple ways to extract the value
    if (result.getChild && result.getChild(columnIndex)) {
      const column = result.getChild(columnIndex);
      if (column.get) {
        return column.get(rowIndex);
      }
    }
    
    // Alternative: try to convert to array and get first value
    if (result.toArray) {
      const array = result.toArray();
      if (array && array.length > rowIndex && array[rowIndex]) {
        const row = array[rowIndex];
        if (typeof row === 'object' && row !== null) {
          // Get first property value if it's an object
          const values = Object.values(row);
          return values.length > columnIndex ? values[columnIndex] : null;
        }
        return row;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting scalar value:', error);
    return null;
  }
}

export async function createTableFromCSVFile(
  db: any,
  tableName: string,
  file: File,
  selectedColumns?: string[],
  disableAutoDetect: boolean = false
): Promise<void> {
  console.log('=== CSV FILE DEBUG INFO ===');
  console.log('File name:', file.name);
  console.log('File size:', file.size, 'bytes');
  console.log('File type:', file.type);
  console.log('Selected columns:', selectedColumns);
  
  try {
    // Convert File to ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    console.log('ArrayBuffer size:', arrayBuffer.byteLength);
    
    // Let's also check the first few bytes of the file
    const firstBytes = new Uint8Array(arrayBuffer.slice(0, 200));
    const preview = new TextDecoder().decode(firstBytes);
    console.log('File content preview (first 200 chars):', preview);
    
    // Register the file with DuckDB (done on database instance, not connection)
    const fileName = `${tableName}_data.csv`;
    console.log('Registering file as:', fileName);
    await db.registerFileBuffer(fileName, new Uint8Array(arrayBuffer));
    console.log('File registered successfully');

    // Use connection for SQL operations
    await withConnection(db, async (connection) => {
      console.log('Got DuckDB connection for CSV operations');
      
      // Drop table if it exists
      console.log('Dropping existing table...');
      await connection.query(`DROP TABLE IF EXISTS ${tableName}`);

      // Create table directly from CSV file using DuckDB's read_csv function
      // Create table directly from CSV file using DuckDB's read_csv function
      let createTableSQL: string;
      let readCsvOptions = `header=true, auto_detect=${!disableAutoDetect}`;

      if (disableAutoDetect) {
        const headers = await getCSVHeaders(file);
        const columnsObject = headers.reduce((acc, h) => {
          acc[h] = 'VARCHAR';
          return acc;
        }, {} as Record<string, string>);
        readCsvOptions += `, columns=${JSON.stringify(columnsObject).replace(/"/g, "'")}, all_varchar=true, ignore_errors=true, escape='"', strict_mode=false`;
      }

      console.log("Creating table from CSV with options:", readCsvOptions);
      if (selectedColumns && selectedColumns.length > 0) {
        // Select only specific columns
        const columnList = selectedColumns.map(col => `"${col}"`).join(', ');
        createTableSQL = `CREATE TABLE ${tableName} AS
          SELECT ${columnList}
          FROM read_csv('${fileName}', ${readCsvOptions})`;
      } else {
        // Select all columns
        createTableSQL = `CREATE TABLE ${tableName} AS
          SELECT * FROM read_csv('${fileName}', ${readCsvOptions})`;
      }

      console.log('Executing CREATE TABLE SQL:', createTableSQL);
      await connection.query(createTableSQL);

      // Get row count for logging
      const countResult = await connection.query(`SELECT COUNT(*) as count FROM ${tableName}`);
      console.log('Table count result raw:', countResult);
      console.log('Table count result toArray():', countResult.toArray());
      
      const rowCount = extractScalarValue(countResult, 0, 0) || 0;
      
      console.log(`Table ${tableName} created with ${rowCount} rows using DuckDB native CSV reader`);
      
      // Let's also check what columns were created
      const columnsResult = await connection.query(`DESCRIBE ${tableName}`);
      console.log('Table schema toArray():', columnsResult.toArray());
    });
  } catch (error) {
    console.error('Failed to create table from CSV file:', error);
    throw new Error(`Failed to create table from CSV file: ${error}`);
  }
}

export async function getCSVHeaders(file: File): Promise<string[]> {
  // For getting headers, we can still use a small sample read
  // This is much more efficient than parsing the entire file
  const text = await file.text();
  const firstLine = text.split('\n')[0];
  if (!firstLine) {
    throw new Error('CSV file appears to be empty');
  }
  
  // Simple CSV header parsing (handles basic cases)
  // For more complex CSVs, we could still use a small PapaParse sample
  const headers = firstLine.split(',').map(header => 
    header.trim().replace(/^["']|["']$/g, '') // Remove quotes
  );
  
  if (headers.length === 0) {
    throw new Error('No headers found in CSV file');
  }
  
  return headers.map((header, index) => 
    header || `Column_${index + 1}` // Handle empty header names
  );
}

export async function createTableFromData(
  db: any,
  tableName: string,
  columns: string[],
  data: (string | null)[][]
): Promise<void> {
  try {
    await withConnection(db, async (connection) => {
      // Drop table if it exists
      await connection.query(`DROP TABLE IF EXISTS ${tableName}`);

      // Determine column types based on data
      const columnDefinitions = columns.map((col, index) => {
        const columnData = data.map(row => row[index]).filter(val => val !== null);
        const sampleValue = columnData[0];
        
        let type = 'VARCHAR';
        if (sampleValue !== undefined) {
          const numValue = Number(sampleValue);
          if (!isNaN(numValue) && isFinite(numValue)) {
            type = numValue % 1 === 0 ? 'INTEGER' : 'DOUBLE';
          } else if (sampleValue === 'true' || sampleValue === 'false') {
            type = 'BOOLEAN';
          }
        }
        
        return `"${col}" ${type}`;
      });

      // Create table
      const createTableSQL = `CREATE TABLE ${tableName} (${columnDefinitions.join(', ')})`;
      await connection.query(createTableSQL);

      // Insert data in batches for better performance
      const batchSize = 1000;
      for (let i = 0; i < data.length; i += batchSize) {
        const batch = data.slice(i, i + batchSize);
        const values = batch.map(row => {
          const formattedRow = row.map(cell => {
            if (cell === null || cell === undefined) return 'NULL';
            
            // Check if it's a number
            const numValue = Number(cell);
            if (!isNaN(numValue) && isFinite(numValue)) {
              return numValue;
            }
            
            // Check if it's a boolean
            if (cell === 'true' || cell === 'false') {
              return cell;
            }
            
            // Escape single quotes for strings
            return `'${String(cell).replace(/'/g, "''")}'`;
          });
          
          return `(${formattedRow.join(', ')})`;
        });

        if (values.length > 0) {
          const insertSQL = `INSERT INTO ${tableName} VALUES ${values.join(', ')}`;
          await connection.query(insertSQL);
        }
      }

      console.log(`Table ${tableName} created with ${data.length} rows`);
    });
  } catch (error) {
    console.error('Failed to create table:', error);
    throw new Error(`Failed to create table: ${error}`);
  }
}

export async function executeQuery(db: any, sql: string): Promise<QueryResult> {
  try {
    return await withConnection(db, async (connection) => {
      const result = await connection.query(sql);
      console.log('Query result raw:', result);
      console.log('Query result toArray():', result.toArray());
      
      // Try to use toArray() first as it's more reliable
      const resultArray = result.toArray();
      if (resultArray && resultArray.length > 0) {
        // Get columns from the first row keys
        const firstRow = resultArray[0];
        const columns = Object.keys(firstRow);
        
        // Convert to rows format
        const rows = resultArray.map((row: any) => 
          columns.map(col => row[col] ?? null)
        );
        
        return {
          columns,
          rows,
          rowCount: resultArray.length
        };
      }
      
      // Fallback to the original method
      const columns = result.schema.fields.map((field: any) => field.name);
      const rows: any[][] = [];
      
      // Convert result to array format
      for (let i = 0; i < result.numRows; i++) {
        const row: any[] = [];
        for (let j = 0; j < columns.length; j++) {
          const value = extractScalarValue(result, j, i);
          row.push(value ?? null);
        }
        rows.push(row);
      }

      return {
        columns,
        rows,
        rowCount: result.numRows
      };
    });
  } catch (error) {
    console.error('Query execution failed:', error);
    throw new Error(`Query failed: ${error}`);
  }
}

export async function exportToCSV(db: any, query: string): Promise<Uint8Array> {
  try {
    const result = await executeQuery(db, query);
    const csvContent = resultToCSV(result);
    return new TextEncoder().encode(csvContent);
  } catch (error) {
    console.error('CSV export failed:', error);
    throw new Error(`CSV export failed: ${error}`);
  }
}

function resultToCSV(result: QueryResult): string {
  const { columns, rows } = result;
  
  // Create CSV header
  const header = columns.map(col => `"${col}"`).join(',');
  
  // Create CSV rows
  const csvRows = rows.map(row => 
    row.map(cell => {
      if (cell === null || cell === undefined) return '';
      const str = String(cell);
      // Escape quotes and wrap in quotes if necessary
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    }).join(',')
  );
  
  return [header, ...csvRows].join('\n');
} 